import { ENV } from '../../../config';
import { normalizeTaxYear } from '../../../shared/utils/NormalizeTaxYear';

export interface TemplateIncomeCategory {
  id: string;
  categoryName: string;
  sortOrder: number;
  description: string;
}

export interface TBAccount {
  accountId: string;
  accountName: string;
  accountType: string;
  amount: number;
  mappedCategoryId: string | null;
}

export interface SaveMappingDto {
  accountId: string;
  accountName: string;
  incomeCategoryId: string;
}

const BASE = `${ENV.API_URL}/api`;

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    tenant: ENV.TENANT_ID ?? '',
  };
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(`API error ${r.status}`);
  const d = await r.json();
  return (d?.data ?? d) as T;
}

export const accountMappingService = {
  getCategories(templateVersionId: number | string): Promise<TemplateIncomeCategory[]> {
    return apiFetch<TemplateIncomeCategory[]>(
      `/account-mapping/categories?templateVersionId=${templateVersionId}`
    );
  },

  getAccounts(
    entityId: number | string,
    templateVersionId: number | string
  ): Promise<TBAccount[]> {
    return apiFetch<TBAccount[]>(
      `/account-mapping/${entityId}/accounts?templateVersionId=${templateVersionId}`
    );
  },

  async saveMappings(
    entityId: number | string,
    templateVersionId: number | string,
    mappings: SaveMappingDto[]
  ): Promise<void> {
    const r = await fetch(`${BASE}/account-mapping/${entityId}/save`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateVersionId, mappings }),
    });
    if (!r.ok) throw new Error(`Save failed: ${r.status}`);
  },

  async resolveTemplateVersionId(
    entityId: number | string,
    taxYear: string
  ): Promise<number> {
    const RESOLUTION_ERROR =
      'Could not resolve template version for account mapping. Ensure a Calculation Profile with an active template exists for this entity.';

    const profileRes = await fetch(
      `${BASE}/calc-profile?partnershipId=${entityId}&taxYear=${normalizeTaxYear(taxYear)}`,
      { headers: authHeaders() }
    );
    if (!profileRes.ok) throw new Error(RESOLUTION_ERROR);
    const profileJson = await profileRes.json();
    const profile = profileJson.data ?? profileJson;
    const sc002 = (profile.items as Array<{
      standardCalculationId: string;
      templatePath: string | null;
    }> | undefined)?.find(i => i.standardCalculationId === 'SC-002');
    if (!sc002?.templatePath) throw new Error(RESOLUTION_ERROR);

    const versionsRes = await fetch(`${BASE}/template`, { headers: authHeaders() });
    if (!versionsRes.ok) throw new Error(RESOLUTION_ERROR);
    const versionsJson = await versionsRes.json();
    const versions = (Array.isArray(versionsJson)
      ? versionsJson
      : (versionsJson.data ?? [])) as Array<{ id: number; blobPath: string }>;
    const match = versions.find(v => v.blobPath === sc002.templatePath);
    if (!match) throw new Error(RESOLUTION_ERROR);
    return match.id;
  },
};
